import React from 'react';
import { NavLink } from 'react-router-dom';
import Navbar from '../components/Landing_Page/Navbar';
import Footer from '../components/Landing_Page/Footer';
import {
  APP_NAME,
  COMPANY_DEFAULT_ADDRESS,
  COMPANY_DEFAULT_EMAIL,
  COMPANY_DEFAULT_WEBSITE,
  COMPANY_LEGAL_NAME,
} from '../lib/brand';

const EFFECTIVE_DATE = 'July 8, 2026';

type SectionProps = {
  title: string;
  children: React.ReactNode;
};

function Section({ title, children }: SectionProps) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-display text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">{title}</h2>
      <div className="space-y-4 text-sm leading-relaxed text-zinc-600 sm:text-base">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zinc-50 font-sans text-zinc-900 antialiased">
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="container mx-auto max-w-3xl px-4 sm:px-6">
          <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Legal</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              Privacy Policy
            </h1>
            <p className="mt-3 text-sm text-zinc-500">
              Effective date: <span className="font-medium text-zinc-700">{EFFECTIVE_DATE}</span>
            </p>
            <p className="mt-6 text-sm leading-relaxed text-zinc-600 sm:text-base">
              This Privacy Policy describes how <strong>{COMPANY_LEGAL_NAME}</strong> ("we", "us", or "our") collects,
              uses, stores, and shares information when you use the <strong>{APP_NAME}</strong> mobile application,
              website, and related services (collectively, the "Service"). The Service is a multi-tenant financial
              operations platform used by finance businesses and their authorized staff and customers.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-zinc-600 sm:text-base">
              By registering for, accessing, or using the Service, you agree to the practices described in this Privacy
              Policy. If you do not agree, please do not use the Service.
            </p>

            <div className="mt-10 border-t border-zinc-100 pt-10">
              <Section title="1. Who This Policy Applies To">
                <p>This policy applies to:</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    <strong>Organization administrators and staff</strong> who create accounts and manage finance
                    operations through {APP_NAME}.
                  </li>
                  <li>
                    <strong>Customers and borrowers</strong> who are given portal access by their finance provider to
                    view loan and payment information.
                  </li>
                  <li>
                    <strong>Visitors</strong> who browse our marketing website, register an organization, or contact us.
                  </li>
                </ul>
                <p>
                  Each organization using {APP_NAME} acts as the controller of its own customer and business data. We
                  process that data on behalf of the organization as a service provider.
                </p>
              </Section>

              <Section title="2. Information We Collect">
                <p>
                  <strong>2.1 Information you provide directly</strong>
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    <strong>Account and profile data:</strong> name, email address, phone number, password, organization
                    name, role, and workspace settings.
                  </li>
                  <li>
                    <strong>Business and finance records:</strong> customer details, loan and EMI information, payment
                    history, employee and payroll data, vehicle and consultancy records, notes, and other operational data
                    entered into the Service.
                  </li>
                  <li>
                    <strong>Documents and media:</strong> applicant photos, KYC documents, company logos, vehicle images,
                    and other files you upload through the app.
                  </li>
                  <li>
                    <strong>Support and communications:</strong> messages, feedback, and contact form submissions.
                  </li>
                  <li>
                    <strong>Subscription and billing details:</strong> selected plan, billing term, payment status, and
                    transaction references. Payment card or UPI details are collected and processed directly by our
                    payment partner and are not stored by us.
                  </li>
                </ul>

                <p>
                  <strong>2.2 Information collected automatically</strong>
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    <strong>Device and usage data:</strong> device type, operating system, browser or app version,
                    IP address, timestamps, pages or screens viewed, and actions taken in the Service.
                  </li>
                  <li>
                    <strong>Authentication data:</strong> session tokens and login timestamps needed to keep you signed
                    in securely.
                  </li>
                  <li>
                    <strong>Local storage:</strong> limited workspace and session data may be cached on your device to
                    support offline access and faster loading.
                  </li>
                </ul>

                <p>
                  <strong>2.3 Information from third parties</strong>
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    <strong>Google Sign-In:</strong> if you choose to sign in with Google, we receive basic profile
                    information such as your name and email address from Google, subject to your Google account
                    settings.
                  </li>
                  <li>
                    <strong>Payment providers:</strong> Razorpay provides us with payment status, transaction IDs, and
                    subscription confirmation details.
                  </li>
                </ul>
              </Section>

              <Section title="3. Permissions Used by the Mobile App">
                <p>The {APP_NAME} Android app may request access to the following device features when you use related features:</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    <strong>Internet:</strong> required to connect to your workspace, sync data, and process
                    subscriptions.
                  </li>
                  <li>
                    <strong>Camera and photo library:</strong> only when you choose to take a photo or upload an image
                    for applicant profiles, vehicle records, logos, or documents.
                  </li>
                  <li>
                    <strong>File storage:</strong> to save exported PDF and Excel reports and to share files through
                    your device when you request a download or share action.
                  </li>
                </ul>
                <p>
                  We do not access your camera, photos, or files unless you initiate the related action in the app. We
                  do not collect precise location data, contacts, SMS, call logs, or microphone audio.
                </p>
              </Section>

              <Section title="4. How We Use Your Information">
                <p>We use the information we collect to:</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>create and manage user accounts and organization workspaces;</li>
                  <li>provide finance management, reporting, customer portal, and subscription features;</li>
                  <li>process subscription payments and maintain billing records;</li>
                  <li>authenticate users and protect against unauthorized access;</li>
                  <li>generate PDF, Excel, and other exports you request;</li>
                  <li>send service-related notifications and operational alerts within the app;</li>
                  <li>respond to support requests and improve the Service;</li>
                  <li>monitor usage, enforce plan limits, and maintain platform security;</li>
                  <li>comply with applicable laws, regulations, and lawful requests.</li>
                </ul>
                <p>
                  If you use WhatsApp sharing, the message is opened in your own WhatsApp application. We do not send
                  WhatsApp messages on your behalf without your explicit action.
                </p>
              </Section>

              <Section title="5. How We Share Information">
                <p>We do not sell your personal information. We may share information only in these situations:</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    <strong>Within your organization:</strong> authorized users in the same workspace can access data
                    according to their role and permissions.
                  </li>
                  <li>
                    <strong>Service providers:</strong> trusted vendors that help us operate the Service, such as cloud
                    hosting, database providers, media storage (for example, Cloudinary), payment processors (Razorpay),
                    and authentication providers (Google), only as needed to perform their services.
                  </li>
                  <li>
                    <strong>Legal and safety:</strong> when required by law, court order, or government request, or to
                    protect the rights, safety, and security of users, our platform, or the public.
                  </li>
                  <li>
                    <strong>Business transfers:</strong> in connection with a merger, acquisition, financing, or sale of
                    assets, subject to appropriate confidentiality protections.
                  </li>
                </ul>
              </Section>

              <Section title="6. Data Storage and Security">
                <p>
                  Your data is stored on secure servers and transmitted using industry-standard encryption (HTTPS/TLS).
                  Passwords are stored using secure hashing methods. Access to production systems is restricted to
                  authorized personnel.
                </p>
                <p>
                  Uploaded images may be stored with our media hosting provider. Organization workspace data is stored in
                  our database infrastructure and is logically separated by tenant.
                </p>
                <p>
                  No method of transmission or storage is completely secure. While we use reasonable safeguards, we
                  cannot guarantee absolute security.
                </p>
              </Section>

              <Section title="7. Data Retention">
                <p>
                  We retain account and workspace data for as long as your organization maintains an active subscription
                  or as needed to provide the Service. If an organization closes its account, we may retain certain
                  records for a limited period to comply with legal, tax, accounting, or dispute-resolution obligations.
                </p>
                <p>
                  You may request deletion of your personal account information by contacting us. Some data may need to
                  remain if your finance organization is required to retain business records under applicable law.
                </p>
              </Section>

              <Section title="8. Your Rights and Choices">
                <p>Depending on your location and role, you may have the right to:</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>access and review personal information we hold about you;</li>
                  <li>request correction of inaccurate or incomplete information;</li>
                  <li>request deletion of personal information, subject to legal and contractual limits;</li>
                  <li>withdraw consent where processing is based on consent;</li>
                  <li>object to or restrict certain processing activities;</li>
                  <li>export data available through your workspace or by contacting support.</li>
                </ul>
                <p>
                  Organization administrators can update most business and user records directly in the app. Customer
                  portal users should contact their finance provider for account changes related to loan records.
                </p>
                <p>
                  To exercise your rights, email us at{' '}
                  <a href={`mailto:${COMPANY_DEFAULT_EMAIL}`} className="font-medium text-emerald-700 hover:underline">
                    {COMPANY_DEFAULT_EMAIL}
                  </a>
                  . We may need to verify your identity before responding.
                </p>
              </Section>

              <Section title="9. Children's Privacy">
                <p>
                  The Service is intended for use by businesses and adults. It is not directed to children under the age
                  of 13, and we do not knowingly collect personal information from children. If you believe a child has
                  provided us personal information, please contact us and we will take appropriate steps to delete it.
                </p>
              </Section>

              <Section title="10. International Data Transfers">
                <p>
                  We are based in India. If you access the Service from outside India, your information may be
                  transferred to, stored in, and processed in India or other countries where our service providers
                  operate. We take steps designed to ensure appropriate protection for such transfers.
                </p>
              </Section>

              <Section title="11. Third-Party Links and Services">
                <p>
                  The Service may link to or integrate with third-party websites or services such as Google, Razorpay,
                  Cloudinary, and WhatsApp. Their privacy practices are governed by their own policies. We encourage you
                  to review those policies before using third-party services.
                </p>
              </Section>

              <Section title="12. Changes to This Privacy Policy">
                <p>
                  We may update this Privacy Policy from time to time. When we do, we will revise the "Effective date"
                  at the top of this page. Material changes may also be communicated through the app or by email where
                  appropriate. Your continued use of the Service after an update means you accept the revised policy.
                </p>
              </Section>

              <Section title="13. Contact Us">
                <p>
                  If you have questions about this Privacy Policy or our data practices, contact:
                </p>
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
                  <p className="font-semibold text-zinc-900">{COMPANY_LEGAL_NAME}</p>
                  <p className="mt-2">{APP_NAME} Privacy Team</p>
                  <p className="mt-2">
                    Email:{' '}
                    <a href={`mailto:${COMPANY_DEFAULT_EMAIL}`} className="font-medium text-emerald-700 hover:underline">
                      {COMPANY_DEFAULT_EMAIL}
                    </a>
                  </p>
                  <p className="mt-1">Website: {COMPANY_DEFAULT_WEBSITE}</p>
                  <p className="mt-1">Address: {COMPANY_DEFAULT_ADDRESS}</p>
                </div>
              </Section>
            </div>

            <div className="mt-10 flex flex-wrap gap-3 border-t border-zinc-100 pt-8">
              <NavLink
                to="/"
                className="inline-flex items-center rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-emerald-300 hover:text-emerald-800"
              >
                Back to home
              </NavLink>
              <NavLink
                to="/login"
                className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                Sign in
              </NavLink>
              <NavLink
                to="/delete-account"
                className="inline-flex items-center rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-rose-300 hover:text-rose-800"
              >
                Delete account
              </NavLink>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
